import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Plus, Edit2, Trash2, Search, DollarSign } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdiantamentoSalarialSection = () => {
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [dialog, setDialog] = useState({ open: false, action: 'create', data: { company_id: '', company_name: '', day_of_month: '', percentage_clt: '', percentage_pj: '' } });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/salary-advances`);
      setItems(res.data);
      const compRes = await axios.get(`${API}/companies`);
      setCompanies(compRes.data);
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      toast.error('Erro ao carregar adiantamentos salariais');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    const d = dialog.data;
    if (!d.company_id) { toast.error('Selecione uma empresa'); return; }
    if (!d.day_of_month || d.day_of_month < 1 || d.day_of_month > 31) { toast.error('Dia do adiantamento deve estar entre 1 e 31'); return; }
    if ((d.percentage_clt === '' || d.percentage_clt < 0) && (d.percentage_pj === '' || d.percentage_pj < 0)) {
      toast.error('Informe ao menos um percentual (CLT ou PJ)'); return;
    }

    const payload = {
      company_id: d.company_id,
      company_name: d.company_name,
      day_of_month: Number(d.day_of_month),
      percentage_clt: d.percentage_clt !== '' ? Number(d.percentage_clt) : null,
      percentage_pj: d.percentage_pj !== '' ? Number(d.percentage_pj) : null,
    };

    try {
      if (dialog.action === 'edit') {
        await axios.put(`${API}/salary-advances/${d.id}`, payload);
        toast.success('Adiantamento salarial atualizado!');
      } else {
        await axios.post(`${API}/salary-advances`, payload);
        toast.success('Adiantamento salarial criado!');
      }
      setDialog({ open: false, action: 'create', data: { company_id: '', company_name: '', day_of_month: '', percentage_clt: '', percentage_pj: '' } });
      fetchData();
    } catch (err) {
      console.error(err);
      toast.error('Erro ao salvar adiantamento salarial');
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.item) return;
    try {
      await axios.delete(`${API}/salary-advances/${deleteDialog.item.id}`);
      toast.success('Adiantamento salarial removido!');
      setDeleteDialog({ open: false, item: null });
      fetchData();
    } catch (err) {
      toast.error('Erro ao remover adiantamento salarial');
    }
  };

  const handleCompanyChange = (companyId) => {
    const company = companies.find((c) => c.id === companyId);
    setDialog((prev) => ({
      ...prev,
      data: { ...prev.data, company_id: companyId, company_name: company ? company.name : '' },
    }));
  };

  const openCreateDialog = () => {
    setDialog({ open: true, action: 'create', data: { company_id: '', company_name: '', day_of_month: '', percentage_clt: '', percentage_pj: '' } });
  };

  const openEditDialog = (item) => {
    setDialog({ open: true, action: 'edit', data: { ...item, percentage_clt: item.percentage_clt ?? '', percentage_pj: item.percentage_pj ?? '' } });
  };

  const filteredData = items.filter((i) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (i.company_name || '').toLowerCase().includes(term);
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            <span>Adiantamento Salarial</span>
          </CardTitle>
          <CardDescription>Configure o percentual de adiantamento salarial por empresa</CardDescription>
        </div>
        <Button onClick={openCreateDialog} className="btn-primary">
          <Plus className="h-4 w-4 mr-2" /> Adicionar
        </Button>
      </CardHeader>
      <CardContent>
        <div className="mb-4 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 h-4 w-4" />
          <Input
            placeholder="Buscar por empresa..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Empresa</TableHead>
                <TableHead>CLT (%)</TableHead>
                <TableHead>PJ (%)</TableHead>
                <TableHead>Dia do Adiantamento</TableHead>
                <TableHead className="text-center">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8">Carregando...</TableCell></TableRow>
              ) : filteredData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                    {searchTerm ? 'Nenhum item encontrado' : 'Nenhum adiantamento cadastrado'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredData.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.company_name}</TableCell>
                      <TableCell>{item.percentage_clt != null ? `${item.percentage_clt}%` : '-'}</TableCell>
                      <TableCell>{item.percentage_pj != null ? `${item.percentage_pj}%` : '-'}</TableCell>
                      <TableCell>Dia {item.day_of_month}</TableCell>
                      <TableCell className="text-center">
                      <div className="flex justify-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(item)}
                          className="text-blue-600"><Edit2 className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeleteDialog({ open: true, item })}
                          className="text-red-600"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      <Dialog open={dialog.open} onOpenChange={(o) => !o && setDialog({ open: false, action: 'create', data: { company_id: '', company_name: '', day_of_month: '', percentage_clt: '', percentage_pj: '' } })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{dialog.action === 'edit' ? 'Editar' : 'Adicionar'} Adiantamento Salarial</DialogTitle>
            <DialogDescription>Preencha as informações abaixo</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Empresa <span className="text-red-500">*</span></Label>
              <Select value={dialog.data.company_id} onValueChange={handleCompanyChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione uma empresa" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Dia do Adiantamento <span className="text-red-500">*</span></Label>
              <Input
                type="number"
                min={1}
                max={31}
                value={dialog.data.day_of_month}
                onChange={(e) => setDialog((p) => ({ ...p, data: { ...p.data, day_of_month: e.target.value } }))}
                placeholder="Ex: 20"
              />
              <p className="text-xs text-slate-400 mt-1">Valor entre 1 e 31</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>CLT (%)</Label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step={0.1}
                  value={dialog.data.percentage_clt}
                  onChange={(e) => setDialog((p) => ({ ...p, data: { ...p.data, percentage_clt: e.target.value } }))}
                  placeholder="Ex: 40"
                />
              </div>
              <div>
                <Label>PJ (%)</Label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step={0.1}
                  value={dialog.data.percentage_pj}
                  onChange={(e) => setDialog((p) => ({ ...p, data: { ...p.data, percentage_pj: e.target.value } }))}
                  placeholder="Ex: 30"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ open: false, action: 'create', data: { company_id: '', company_name: '', day_of_month: '', percentage_clt: '', percentage_pj: '' } })}>Cancelar</Button>
            <Button onClick={handleSave} className="btn-primary">{dialog.action === 'edit' ? 'Atualizar' : 'Salvar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialog.open} onOpenChange={(o) => setDeleteDialog({ open: o, item: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja remover o adiantamento salarial da empresa <strong>{deleteDialog.item?.company_name}</strong>?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, item: null })}>Cancelar</Button>
            <Button variant="destructive" onClick={handleDelete}>Remover</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default AdiantamentoSalarialSection;
